import type { ColDef, ICellRendererParams } from 'ag-grid-community';
import { formatDate, formatRelativeTime } from '@utils/format';
import { ActiveBadge } from './components/cells/ActiveBadge';
import { TagRowActions } from './components/cells/TagRowActions';
import type { Tag } from './schema';

export interface TagRowActionHandlers {
  onEdit: (row: Tag) => void;
}

// `colId` on sortable columns matches the API's sortBy key. TagsGrid reads
// it from column state and sends it as `sortBy`.
export function buildTagsColumns(
  actions: TagRowActionHandlers,
): ColDef<Tag>[] {
  return [
    {
      colId: 'code',
      field: 'code',
      headerName: 'Code',
      flex: 1,
      minWidth: 160,
      sortable: true,
      pinned: 'left',
      lockPinned: true,
      cellRenderer: (p: ICellRendererParams<Tag>) =>
        p.data ? (
          <code className="rounded bg-surface-muted px-1.5 py-0.5 font-mono text-[12.5px] text-foreground">
            {p.data.code}
          </code>
        ) : null,
    },
    {
      colId: 'name',
      field: 'name',
      headerName: 'Name',
      flex: 2,
      minWidth: 220,
      sortable: true,
    },
    {
      field: 'isActive',
      headerName: 'Active',
      width: 130,
      cellRenderer: ActiveBadge,
    },
    {
      colId: 'created',
      field: 'createdAt',
      headerName: 'Created',
      width: 200,
      sortable: true,
      cellRenderer: (p: ICellRendererParams<Tag>) =>
        p.data ? (
          <div className="flex flex-col leading-tight">
            <span className="text-[13px] font-semibold text-foreground">
              {formatDate(p.data.createdAt)}
            </span>
            <span className="mt-0.5 text-[11px] text-foreground-muted">
              Updated {formatRelativeTime(p.data.updatedAt)}
            </span>
          </div>
        ) : null,
    },
    {
      headerName: 'Actions',
      width: 100,
      sortable: false,
      filter: false,
      pinned: 'right',
      cellRenderer: (p: ICellRendererParams<Tag>) =>
        p.data ? <TagRowActions row={p.data} {...actions} /> : null,
    },
  ];
}
